import React, { useContext, useMemo, useState, useRef, useEffect } from "react";
import { DialogContext } from "providers/DialogProvider";
import { ModalFuncProps } from "@bit-design/react/es/modal";
import { Modal as BitModal, Drawer as BitDrawer } from "@bit-design/react";
import { CustomModalFunc } from "cointr";
import Loading from "components/common/Loading";
import { useRecoilValue } from "recoil";
import { ThemeSelector } from "store/data/Base";
import {
  MAIN_DARK,
  MAIN_DEEP_WHITE,
  GRAY_7d7d7d,
  NO_THEME_GREY,
  THEME,
} from "constants/colors";
import { DeviceType, GetDeviceInfoHooks } from "../util";
import classNames from "classnames";
import Draggable, { DraggableBounds } from "react-draggable";
// 抄袭@bit-design/react的模态框钩子模式，复写了内层只能用confirm模式

export function useDialog(): CustomModalFunc {
  return useContext(DialogContext);
}

interface HookModalProps {
  afterClose: () => void;
  config: ModalFuncProps & {
    forceDialog?: boolean;
    padding?: boolean;
    resetBodyPadding?: boolean;
    drawerMask?: boolean;
    closeIcon?: React.ReactNode;
    drawerTitle?: React.ReactNode;
    placement?: "top" | "right" | "bottom" | "left";
    drawerOnClose?: () => void;
    height?: string | number;
    onClose?: () => void;
    cancelCallback?: () => void;
    isBitDesign?: boolean;
    // 逐步替换Modal的标识
    isIteration?: boolean;
    maskClosable?: boolean;
    dragId?: string; // 如果modal模式下需要拖动，需要传入一个对应传入元素的id，用来记忆拖动位置。
  };
  callback?: (e?) => void;
}

interface HookModalRef {
  destroy: () => void;
  update: (config: ModalFuncProps) => void;
  callback?: (e?) => void;
}

const _HookModal: React.ForwardRefRenderFunction<
  HookModalRef,
  HookModalProps
> = (props, ref) => {
  const { config, afterClose, callback } = props;
  const [visible, setVisible] = useState(true);
  const [innerConfig, setInnerConfig] = useState(config);
  const screenInfo = GetDeviceInfoHooks();
  const {
    forceDialog,
    padding,
    drawerMask = true,
    resetBodyPadding,
    closeIcon,
    className,
    drawerOnClose,
    onClose,
    cancelCallback,
    isBitDesign,
    isIteration,
    dragId,
  } = config;

  // 拖动modal相关---start
  const draggleRef = useRef(null);
  const commondragHandlerStyle = {
    cursor: "move",
    pointerEvents: "all",
    position: "absolute",
    top: "0px",
    height: "18px",
    width: "100%",
    zIndex: 1,
    opacity: 0.5,
  };
  const dragHandlerStyle = {
    ...commondragHandlerStyle,
    borderRadius: "3px 3px 0px 0px",
  };
  const bitDesignDragHandlerStyle = {
    ...commondragHandlerStyle,
    borderRadius: "15px 15px 0px 0px",
  };

  const dragKeyPrefix = "_modal_drag_position";

  let initialPosition = {};
  const getDragPosition = (id) => {
    let res;
    try {
      res = JSON.parse(localStorage.getItem(`${dragKeyPrefix}_${id}`));
    } catch (e) {
      res = {};
    }
    return res;
  };

  const saveDragPosition = (id, position) =>
    localStorage.setItem(`${dragKeyPrefix}_${id}`, JSON.stringify(position));

  // 拖动modal相关 ---end

  function close(e?) {
    setVisible(false);
    onClose && onClose?.();
    drawerOnClose && drawerOnClose();
    callback && callback(e);
  }

  // 相当于对ref增加实例方法
  React.useImperativeHandle(ref, () => ({
    destroy: close,
    update: (newConfig: ModalFuncProps) => {
      setInnerConfig((originConfig) => ({
        ...originConfig,
        ...newConfig,
      }));
    },
  }));
  const isDarkTheme = useRecoilValue(ThemeSelector) === THEME.DARK;

  const darkTheme = {
    background: isDarkTheme ? MAIN_DARK : MAIN_DEEP_WHITE,
    color: isDarkTheme ? GRAY_7d7d7d : NO_THEME_GREY,
    borderRadius: "4px",
  };
  const drawerTheme = {
    padding: resetBodyPadding ? "0" : "24px",
  };
  const darkThemetop = {
    padding: padding ? "8px 0px" : "16px 24px",
  };

  const curCloseIcon = (
    <div data-testid={"TradeCommonDialogCloseButton"}>
      {closeIcon || <i className="iconfont icon-guanbi1" />}
    </div>
  );

  //使用抽屉模式
  const isDrawer = screenInfo.deviceType === DeviceType.PHONE && !forceDialog;

  const curConfig = useMemo(() => {
    if (isDrawer) {
      const config = { ...innerConfig };
      delete config.drawerTitle;
      return config;
    }
    return innerConfig;
  }, [innerConfig, isDrawer]);

  const InnerDrawer = BitDrawer;
  const InnerModal = BitModal;
  const hasTitleProp = useMemo(() => {
    return Boolean(config?.drawerTitle) || Boolean(curConfig.title);
  }, [config?.drawerTitle, curConfig.title]);

  if (isIteration) {
    return (
      <InnerModal
        title={config?.drawerTitle}
        onCancel={(e) => {
          close(e);
          cancelCallback?.();
        }}
        {...(curConfig as any)}
        centered={isBitDesign ? true : curConfig.centered}
        closeIcon={curCloseIcon}
        open={visible}
        footer={null}
        modalRender={(modal) =>
          dragId ? (
            <Draggable handle={".drag-handler"} nodeRef={draggleRef}>
              <div ref={draggleRef} className={"relative"}>
                <div
                  className={"drag-handler !hover:bg-cyan-600"}
                  style={
                    isBitDesign
                      ? bitDesignDragHandlerStyle
                      : (dragHandlerStyle as any)
                  }
                ></div>
                {modal}
              </div>
            </Draggable>
          ) : (
            modal
          )
        }
        afterClose={afterClose}
      >
        <React.Suspense fallback={<Loading size={"normal"} />}>
          {config.content}
        </React.Suspense>
      </InnerModal>
    );
  }

  return isDrawer ? (
    <InnerDrawer
      className={classNames(
        "dialogs-drawer",
        isBitDesign && !hasTitleProp
          ? `
                        relative
                        [&_.bit-drawer-header]:!pt-0
                        [&_.bit-drawer-header]:!pb-[16px]
                        [&_.bit-drawer-header-title]:!h-0
                    `
          : "",
        isBitDesign
          ? `
                        [&_.bit-drawer-close]:!absolute
                        [&_.bit-drawer-close]:!end-[12px]
                        [&_.bit-drawer-close]:!top-[12px]
                        [&_.bit-drawer-close]:!flex
                        [&_.bit-drawer-close]:!items-center
                        [&_.bit-drawer-close>span.bit-drawer-close-icon]:!flex
                        [&_.bit-drawer-close>span.bit-drawer-close-icon]:!items-center`
          : "",
        className
      )}
      bodyStyle={
        isBitDesign ? undefined : Object.assign({}, darkTheme, drawerTheme)
      }
      headerStyle={isBitDesign ? undefined : darkThemetop}
      title={config?.drawerTitle}
      placement={"bottom"}
      onClose={close}
      height={"auto"}
      {...curConfig}
      open={visible}
      mask={drawerMask}
    >
      <React.Suspense fallback={<Loading size={"normal"} />}>
        {config.content}
      </React.Suspense>
    </InnerDrawer>
  ) : (
    <InnerModal
      width={isBitDesign ? "480px" : undefined}
      className={classNames(className)}
      wrapClassName={
        isBitDesign
          ? `[&_div.bit-modal-content]:max-h-[calc(100vh-32px)] [&_.bit-modal-body]:!py-0`
          : curConfig.wrapClassName
      }
      centered={isBitDesign ? true : curConfig.centered}
      bodyStyle={isBitDesign ? undefined : darkTheme}
      onCancel={(e) => {
        close(e);
        cancelCallback?.();
      }}
      {...(curConfig as any)}
      open={visible}
      closeIcon={curCloseIcon}
      footer={null}
      modalRender={(modal) =>
        dragId ? (
          <Draggable handle={".drag-handler"} nodeRef={draggleRef}>
            <div ref={draggleRef} className={"relative"}>
              <div
                className={"drag-handler !hover:bg-cyan-600"}
                style={
                  isBitDesign
                    ? bitDesignDragHandlerStyle
                    : (dragHandlerStyle as any)
                }
              ></div>
              {modal}
            </div>
          </Draggable>
        ) : (
          modal
        )
      }
      afterClose={afterClose}
    >
      <React.Suspense fallback={<Loading size={"normal"} />}>
        {config.content}
      </React.Suspense>
    </InnerModal>
  );
};

const HookModal = React.forwardRef(_HookModal);

let modalId = 0;

export function useModal(): [CustomModalFunc, React.ReactElement] {
  const [elements, patchElement] = usePatchElement();
  const theme = useRecoilValue(ThemeSelector) || THEME.DARK;

  function getConfirmFunc(): CustomModalFunc {
    return function hookConfirm<T>(content, props: T, config, callback) {
      modalId++;

      const modalRef = React.createRef<HookModalRef>();
      const destroy = () => {
        if (modalRef.current) {
          modalRef.current.destroy();
        }
      };

      props = props || ({} as T);
      const update = (newConfig: ModalFuncProps) => {
        if (modalRef.current) {
          modalRef.current.update(newConfig);
        }
      };
      config.content = React.createElement(content, {
        close: destroy,
        ...props,
        theme,
        update,
      });
      config.drawerTitle =
        config.title || content.title
          ? React.createElement(config.title || content.title, props) // 标题传参props
          : null;
      let close: Function;
      const modal = (
        <HookModal
          key={`modal-${modalId}`}
          ref={modalRef}
          afterClose={() => close()}
          config={config}
          callback={callback}
        />
      );

      close = patchElement(modal);

      return {
        destroy,
        update,
      };
    };
  }

  return [getConfirmFunc(), <>{elements}</>];
}

export function usePatchElement(): [
  React.ReactElement[],
  (element: React.ReactElement) => Function,
] {
  const [elements, setElements] = useState<React.ReactElement[]>([]);

  function patchElement(element: React.ReactElement) {
    setElements((originElements) => [...originElements, element]);

    return () => {
      setElements((originElements) =>
        originElements.filter((ele) => ele !== element)
      );
    };
  }

  return [elements, patchElement];
}
